import React, { useEffect, useMemo, useState } from "react";
import {
  Wallet, RefreshCw, XCircle, Clock, BarChart3, Search, X, Download, ExternalLink, FileText, Copy, ChevronLeft, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Kpi, Th, EmptyState, Spinner, PaymentStatusPill, Pill, formatEUR, formatDate, formatDateTime,
  eventTypeLabel, PLAN_LABEL,
} from "./adminUi";

export const DEFAULT_PAY_FILTERS = { q: "", status: "all", plan: "all", cycle: "all" };
const LIMIT = 25;

function copy(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text).then(() => toast.success("Copiato")).catch(() => {});
}

function DetailRow({ label, children, mono, copyValue }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-b-0 text-sm">
      <span className="text-neutral-500 shrink-0">{label}</span>
      <span className={`text-right min-w-0 break-all ${mono ? "font-mono text-xs" : ""} inline-flex items-center gap-1.5`}>
        {children ?? "—"}
        {copyValue && <button type="button" onClick={() => copy(copyValue)} className="p-0.5 text-neutral-400 hover:text-neutral-800"><Copy className="w-3 h-3" /></button>}
      </span>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid, width = "w-36" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-11 ${width} rounded-md bg-white`} data-testid={testid}>
        <div className="text-left leading-tight"><div className="text-[10px] text-neutral-500">{label}</div><SelectValue /></div>
      </SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function PaymentDetail({ p, onClose, onOpenUser }) {
  const cycle = p.cycle === "yearly" ? "Annuale" : p.cycle === "monthly" ? "Mensile" : "—";
  const amount = p.status === "paid" ? p.amount_paid : p.amount_due;
  return (
    <aside className="bg-white border border-border rounded-md shadow-[0_1px_2px_rgba(0,0,0,0.03)] p-5 space-y-4 xl:sticky xl:top-4" data-testid="payment-detail-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-heading text-lg">Dettagli pagamento</h3>
        <button type="button" className="p-1.5 rounded-full hover:bg-neutral-100" onClick={onClose} data-testid="payment-detail-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center justify-between">
        <PaymentStatusPill status={p.status} />
        <span className="text-xs text-neutral-500">{formatDateTime(p.event_at)}</span>
      </div>
      <div>
        <div className="font-serif-heading text-3xl tabular-nums">{formatEUR(amount)}</div>
        <div className="text-sm text-neutral-600">{PLAN_LABEL[p.plan] || "—"} – Abbonamento {cycle.toLowerCase()}</div>
      </div>
      <div>
        <DetailRow label="Invoice ID" mono copyValue={p.invoice_id}>{p.invoice_id}</DetailRow>
        <DetailRow label="Numero fattura">{p.invoice_number || "—"}</DetailRow>
        <DetailRow label="Tipo evento">{eventTypeLabel(p)}</DetailRow>
        <DetailRow label="Cliente">
          <span className="text-right">
            <span className="block">{p.user_name || "—"}</span>
            <span className="block text-xs text-neutral-500">{p.user_email || "—"}</span>
            {p.user_id && <button type="button" onClick={() => onOpenUser(p.user_id)} className="text-xs text-primary hover:underline" data-testid="payment-detail-open-user">Apri dettaglio cliente →</button>}
          </span>
        </DetailRow>
        <DetailRow label="Piano">{PLAN_LABEL[p.plan] || "—"}</DetailRow>
        <DetailRow label="Ciclo">{cycle}</DetailRow>
        <DetailRow label="Importo dovuto">{formatEUR(p.amount_due)}</DetailRow>
        <DetailRow label="Importo pagato">{formatEUR(p.amount_paid)}</DetailRow>
        <DetailRow label="Valuta">{(p.currency || "eur").toUpperCase()}</DetailRow>
        {p.paid_at && <DetailRow label="Pagato il">{formatDateTime(p.paid_at)}</DetailRow>}
        {p.failed_at && <DetailRow label="Fallito il">{formatDateTime(p.failed_at)}</DetailRow>}
        <DetailRow label="Stripe Customer ID" mono copyValue={p.stripe_customer_id}>{p.stripe_customer_id || "—"}</DetailRow>
        <DetailRow label="Subscription ID" mono copyValue={p.stripe_subscription_id}>{p.stripe_subscription_id || "—"}</DetailRow>
      </div>
      <div className="flex flex-col gap-2">
        {p.hosted_invoice_url && (
          <a href={p.hosted_invoice_url} target="_blank" rel="noreferrer" className="h-10 inline-flex items-center justify-center gap-2 rounded-md border border-primary/40 text-sm text-neutral-900 hover:bg-primary/5" data-testid="payment-detail-stripe-link">
            Vedi fattura Stripe <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        {p.invoice_pdf && (
          <a href={p.invoice_pdf} target="_blank" rel="noreferrer" className="h-10 inline-flex items-center justify-center gap-2 rounded-md border border-border text-sm hover:bg-neutral-50" data-testid="payment-detail-pdf-link">
            <FileText className="w-3.5 h-3.5" /> PDF
          </a>
        )}
      </div>
    </aside>
  );
}

export default function AdminPayments({ period, initialFilters, onOpenUser, refreshKey }) {
  const [filters, setFilters] = useState({ ...DEFAULT_PAY_FILTERS, ...(initialFilters || {}) });
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);

  useEffect(() => { setFilters((f) => ({ ...f, ...(initialFilters || {}) })); setPage(0); }, [initialFilters]);
  useEffect(() => { setPage(0); }, [filters.q, filters.status, filters.plan, filters.cycle, period]);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.q) params.set("q", filters.q);
        ["status","plan","cycle"].forEach((k) => { if (filters[k] && filters[k] !== "all") params.set(k, filters[k]); });
        params.set("date_from", new Date(Date.now() - period * 86400000).toISOString());
        params.set("limit", String(LIMIT));
        params.set("skip", String(page * LIMIT));
        const [p, s] = await Promise.all([api.get(`/admin/billing-payments?${params.toString()}`), api.get("/admin/stats")]);
        if (!alive) return;
        setData(p.data); setStats(s.data);
      } catch (err) { toast.error(err.response?.data?.detail || err.message); }
      finally { alive && setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [filters, period, page, refreshKey]);

  const rows = useMemo(() => data?.payments || [], [data]);
  const k = data?.kpis || {};
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const mrr = stats?.business?.mrr || 0;

  const exportCsv = () => {
    const head = ["data","cliente","email","tipo_evento","piano","ciclo","importo","stato","invoice_id","invoice_number"];
    const lines = rows.map((p) => [
      p.event_at || "", p.user_name || "", p.user_email || "", eventTypeLabel(p), p.plan || "", p.cycle || "",
      String(p.status === "paid" ? p.amount_paid : p.amount_due), p.status || "", p.invoice_id, p.invoice_number || "",
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `pagamenti_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const selectedRow = useMemo(() => rows.find((r) => r.invoice_id === selected) || null, [rows, selected]);

  return (
    <div className="space-y-6" data-testid="admin-payments">
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Kpi icon={Wallet} label={`Incassato ${period} gg`} value={formatEUR(k.collected || 0)} sub="pagamenti Stripe riusciti" tone="ok" testid="pay-kpi-collected" />
        <Kpi icon={RefreshCw} label="Rinnovi riusciti" value={k.renewals_ok ?? 0} sub="billing_reason: rinnovo" tone="ok" testid="pay-kpi-renewals" />
        <Kpi icon={XCircle} label="Pagamenti falliti" value={k.failed_count ?? 0} sub="nel periodo" tone="fail" testid="pay-kpi-failed" onClick={() => setFilters({ ...filters, status: "failed" })} />
        <Kpi icon={Clock} label="Past due" value={k.past_due_users ?? 0} sub="account con rinnovo fallito" tone="warn" testid="pay-kpi-pastdue" />
        <Kpi icon={BarChart3} label="MRR" value={formatEUR(mrr)} sub="solo Stripe reali" tone="primary" testid="pay-kpi-mrr" />
        <Kpi icon={BarChart3} label="ARR" value={formatEUR(mrr * 12)} sub="MRR × 12" testid="pay-kpi-arr" />
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect label="Stato" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} testid="pay-filter-status" options={[["all","Tutti gli stati"],["paid","Pagato"],["failed","Fallito"]]} width="w-40" />
        <FilterSelect label="Piano" value={filters.plan} onChange={(v) => setFilters({ ...filters, plan: v })} testid="pay-filter-plan" options={[["all","Tutti i piani"],["basic","Basic"],["pro","Pro"],["ai_manager","AI Manager"]]} width="w-40" />
        <FilterSelect label="Ciclo" value={filters.cycle} onChange={(v) => setFilters({ ...filters, cycle: v })} testid="pay-filter-cycle" options={[["all","Tutti i cicli"],["monthly","Mensile"],["yearly","Annuale"]]} width="w-36" />
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" strokeWidth={2} />
          <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Cerca per cliente, invoice ID…" className="pl-9 h-11 rounded-md bg-white" data-testid="pay-search-input" />
        </div>
        {(filters.q || filters.status !== "all" || filters.plan !== "all" || filters.cycle !== "all") && (
          <Button variant="ghost" className="h-11 rounded-md text-neutral-600" onClick={() => setFilters(DEFAULT_PAY_FILTERS)} data-testid="pay-reset-filters"><X className="w-3.5 h-3.5 mr-1" /> Reimposta</Button>
        )}
      </div>

      <div className={`grid grid-cols-1 gap-4 ${selectedRow ? "xl:grid-cols-[minmax(0,1fr)_360px]" : ""}`}>
        <section className="bg-white border border-border rounded-md overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.03)] min-w-0">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
            <h2 className="font-serif-heading text-lg">Pagamenti <span className="text-neutral-400">({total})</span></h2>
            <Button variant="outline" className="rounded-md h-9" onClick={exportCsv} disabled={rows.length === 0} data-testid="pay-export-csv"><Download className="w-3.5 h-3.5 mr-2" /> Esporta CSV</Button>
          </div>
          {loading && !data ? <Spinner /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-neutral-50/60">
                    <Th>Data</Th><Th>Cliente</Th><Th>Tipo evento</Th><Th>Piano</Th><Th>Ciclo</Th><Th align="right">Importo</Th><Th>Stato</Th><Th>Origine</Th><Th>Invoice</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.invoice_id} onClick={() => setSelected(p.invoice_id)} className={`border-b border-border last:border-b-0 cursor-pointer transition-colors ${selected === p.invoice_id ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-neutral-50/80"}`} data-testid={`pay-row-${p.invoice_id}`}>
                      <td className="py-3 px-4 tabular-nums whitespace-nowrap text-neutral-700">{formatDate(p.event_at)}</td>
                      <td className="py-3 px-4"><div className="font-medium truncate max-w-[200px]">{p.user_name || "—"}</div><div className="text-xs text-neutral-500 truncate max-w-[200px]">{p.user_email || "—"}</div></td>
                      <td className="py-3 px-4 text-neutral-700 whitespace-nowrap">{eventTypeLabel(p)}</td>
                      <td className="py-3 px-4 text-neutral-700 whitespace-nowrap">{PLAN_LABEL[p.plan] || "—"}</td>
                      <td className="py-3 px-4 text-neutral-700 whitespace-nowrap">{p.cycle === "yearly" ? "Annuale" : p.cycle === "monthly" ? "Mensile" : "—"}</td>
                      <td className="py-3 px-4 text-right tabular-nums font-medium whitespace-nowrap">{formatEUR(p.status === "paid" ? p.amount_paid : p.amount_due)}</td>
                      <td className="py-3 px-4"><PaymentStatusPill status={p.status} /></td>
                      <td className="py-3 px-4 text-xs text-neutral-700">Stripe</td>
                      <td className="py-3 px-4 text-xs text-neutral-600 font-mono whitespace-nowrap">{p.invoice_number || p.invoice_id?.slice(0, 14) || "—"}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={9}><EmptyState>Nessun pagamento nel periodo selezionato.</EmptyState></td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-5 py-3 border-t border-border flex items-center justify-between text-xs text-neutral-500">
            <span data-testid="pay-counter">Mostra {rows.length} di {total}</span>
            <div className="flex items-center gap-1">
              <button type="button" className="p-1.5 rounded-md border border-border disabled:opacity-40 hover:bg-neutral-50" disabled={page <= 0} onClick={() => setPage(page - 1)} data-testid="pay-page-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
              <span className="px-2 tabular-nums">{page + 1} / {pages}</span>
              <button type="button" className="p-1.5 rounded-md border border-border disabled:opacity-40 hover:bg-neutral-50" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} data-testid="pay-page-next"><ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </section>
        {selectedRow && <PaymentDetail p={selectedRow} onClose={() => setSelected(null)} onOpenUser={onOpenUser} />}
      </div>
      {total > 0 && <div className="text-[11px] text-neutral-400">Fonte: ledger permanente <Pill tone="muted">stripe_billing_payments</Pill> — un solo record per invoice, failed → paid non duplica.</div>}
    </div>
  );
}
